/**
 * Anzeige in der Mail-Vorschau (iframe): Änderungsvorschläge wie in Word
 * („Änderungen nachverfolgen“ — gestrichen / eingefügt) und markierte
 * Textstellen von Kommentaren.
 *
 * Arbeitet auf einem Anzeige-Dokument, das danach nicht gespeichert wird.
 * Reihenfolge: erst aenderungenEinblenden, dann zitatMarkieren.
 */
import { bereinigen } from './anwenden';
import {
  ankerTextPasst, blockAusListe, blockText, normalisieren, OHNE_TEXT, struktursignatur, textbloecke, textKnoten,
  type Aenderung, type BlockSchluessel, type TextBlock,
} from './bloecke';
import { wortDiff, type DiffTeil } from './diff';
import { abschnittFinden } from './abschnitte';

/** Stil fürs iframe: gestrichen rot, eingefügt grün — mit eigener Fläche, damit es auch auf dem roten Kopf lesbar bleibt. */
export const ZA_STIL = `
del.za-weg{color:#912018!important;background:#FEE4E2!important;text-decoration:line-through!important;text-decoration-thickness:2px!important;text-decoration-color:#D92D20!important;border-radius:3px;padding:0 1px}
ins.za-neu{color:#085D3A!important;background:#DCFAE6!important;text-decoration:underline!important;text-decoration-thickness:2px!important;text-decoration-color:#17B26A!important;text-underline-offset:2px;border-radius:3px;padding:0 1px}
ins.za-neu.za-format{display:inline;color:inherit!important;background:rgba(220,250,230,.55)!important;text-decoration:none!important;outline:2px dotted #17B26A;outline-offset:1px}
del.za-weg *{color:inherit!important;background:transparent!important}
[data-za-ab-text]{text-decoration:line-through!important;text-decoration-thickness:2px!important;text-decoration-color:#D92D20!important}
[data-za-feedback]{outline:2px dashed rgba(109,23,35,.35);outline-offset:3px;border-radius:4px;cursor:pointer;transition:outline-color .15s ease}
[data-za-feedback]:hover,[data-za-feedback].za-aktiv{outline-color:#6D1723;outline-style:solid}
mark.za-zitat{background:#FDE68A!important;color:inherit!important;border-radius:2px;box-shadow:inset 0 -2px 0 #F59E0B;cursor:pointer;padding:0}
mark.za-zitat.za-aktiv{background:#FCD34D!important;box-shadow:inset 0 -2px 0 #B45309}
`;

const STIL_ID = 'za-stil';

/** Fügt ZA_STIL einmal ins Dokument ein. */
export function stilEinfuegen(doc: Document): void {
  if (doc.getElementById(STIL_ID)) return;
  const st = doc.createElement('style');
  st.id = STIL_ID;
  st.textContent = ZA_STIL;
  (doc.head ?? doc.documentElement).appendChild(st);
}

function diffFragment(doc: Document, teile: DiffTeil[]): DocumentFragment {
  const f = doc.createDocumentFragment();
  for (const t of teile) {
    if (t.typ === 'gleich') { f.appendChild(doc.createTextNode(t.text)); continue; }
    const e = doc.createElement(t.typ === 'weg' ? 'del' : 'ins');
    e.className = t.typ === 'weg' ? 'za-weg' : 'za-neu';
    e.textContent = t.text;
    f.appendChild(e);
  }
  return f;
}

/** Ergeben die neuen Textstücke im Block den Text, den die Karte zeigt? */
function knotenPassen(b: TextBlock, nachher: string[], nachherText: string): boolean {
  const kopie = b.el.cloneNode(true) as Element;
  textKnoten(kopie).forEach((t, k) => { t.data = nachher[k] ?? ''; });
  return blockText(kopie) === normalisieren(nachherText);
}

function zeigen(doc: Document, b: TextBlock, a: Aenderung): void {
  const nachher = a.nachher_knoten;
  if (nachher && nachher.length === a.vorher_knoten.length && b.knoten.length === nachher.length
    && b.knoten.every((t, k) => t.data === a.vorher_knoten[k]) && knotenPassen(b, nachher, a.nachher_text)) {
    // gleiche Struktur: je Textknoten vergleichen, Formatierung bleibt sichtbar
    b.knoten.forEach((t, k) => {
      if (nachher[k] !== t.data) t.replaceWith(diffFragment(doc, wortDiff(t.data, nachher[k])));
    });
    return;
  }
  if (typeof a.nachher_html === 'string') {
    const neu = bereinigen(a.nachher_html);
    const gleicherText = normalisieren(a.vorher_text) === normalisieren(a.nachher_text);
    if (gleicherText || gestaltungAnders(doc, b.el.innerHTML, neu)) {
      // Formatierung geändert (fett, Farbe, Link …): neue Fassung so, wie sie
      // aussehen wird, gepunktet umrandet — bei anderem Text davor der alte gestrichen
      const ins = doc.createElement('ins');
      ins.className = 'za-neu za-format';
      ins.title = 'Formatierung geändert';
      const tpl = doc.createElement('template');
      tpl.innerHTML = neu;
      ins.appendChild(tpl.content);
      if (gleicherText) b.el.replaceChildren(ins);
      else b.el.replaceChildren(diffFragment(doc, [{ typ: 'weg', text: a.vorher_text }, { typ: 'gleich', text: ' ' }]), ins);
      return;
    }
  }
  b.el.replaceChildren(diffFragment(doc, wortDiff(a.vorher_text, a.nachher_text)));
}

/** Unterscheiden sich zwei Stücke HTML in der Gestaltung — mehr als Zeilenumbrüche? */
function gestaltungAnders(doc: Document, vorherHtml: string, nachherSauber: string): boolean {
  const sig = (html: string) => {
    const huelle = doc.createElement('div');
    huelle.innerHTML = html;
    return struktursignatur(huelle, true).replace(/<br><\/br>/g, '');
  };
  return sig(bereinigen(vorherHtml)) !== sig(nachherSauber);
}

export interface Eingeblendet {
  /** id des Eintrags (Mail_feedback.id). */
  id: string;
  /** Index der Änderung im Eintrag. */
  index: number;
  /** Block in der Vorschau (für die Position der Karte); null = nicht mehr gefunden. */
  el: HTMLElement | null;
  /** false: nicht gefunden oder der Block zeigt schon eine andere Änderung. */
  gezeigt: boolean;
}

/**
 * Zeigt offene Änderungen (ergebnis fehlt oder 'offen') in der Vorschau:
 * gestrichen `<del class="za-weg">`, eingefügt `<ins class="za-neu">`; der Block
 * bekommt data-za-feedback="<id>" und data-za-index. Je Block wird die erste
 * Änderung gezeigt.
 */
export function aenderungenEinblenden(doc: Document, eintraege: { id: string; aenderungen: Aenderung[] }[]): Eingeblendet[] {
  stilEinfuegen(doc);
  const bloecke = textbloecke(doc);
  // erst alle Blöcke suchen, dann ändern (das Einblenden verändert die Blocktexte)
  const plan: { id: string; index: number; a: Aenderung; b: TextBlock | null; ab: HTMLElement | null }[] = [];
  for (const e of eintraege) {
    (e.aenderungen ?? []).forEach((a, index) => {
      if (a.ergebnis && a.ergebnis !== 'offen') return;
      const ab = a?.entfernen ? abschnittFinden(doc, a) : null;
      plan.push({ id: e.id, index, a, b: a?.block && !a.entfernen ? blockAusListe(doc, bloecke, a.block, a.vorher_text) : null, ab });
    });
  }
  const belegt = new Set<Element>();
  return plan.map(({ id, index, a, b, ab }) => {
    if (a?.entfernen) {
      // ganzer Abschnitt: Texte durchgestrichen, die Fläche zeichnet die Bühne
      if (!ab) return { id, index, el: null, gezeigt: false };
      if (belegt.has(ab)) return { id, index, el: ab, gezeigt: false };
      belegt.add(ab);
      ab.setAttribute('data-za-ab-text', '');
      textbloecke(ab).forEach((t) => t.el.setAttribute('data-za-ab-text', ''));
      ab.setAttribute('data-za-abschnitt', '');
      ab.setAttribute('data-za-feedback', id);
      ab.setAttribute('data-za-index', String(index));
      return { id, index, el: ab, gezeigt: true };
    }
    if (!b) return { id, index, el: null, gezeigt: false };
    if (belegt.has(b.el)) return { id, index, el: b.el, gezeigt: false };
    belegt.add(b.el);
    zeigen(doc, b, a);
    b.el.setAttribute('data-za-feedback', id);
    b.el.setAttribute('data-za-index', String(index));
    return { id, index, el: b.el, gezeigt: true };
  });
}

// ── Markierte Textstellen ────────────────────────────────────────────────────

/** Sichtbarer Text eines Blocks (ohne gestrichene Stellen) mit Zuordnung Zeichen → Textknoten. */
function textKarte(el: Element): { text: string; stellen: ([Text, number] | null)[]; knoten: Text[] } {
  const knoten: Text[] = [];
  let text = '';
  const stellen: ([Text, number] | null)[] = [];
  let leer = true; // letztes Zeichen war Leerraum (auch am Anfang)
  const laufen = (e: Node) => {
    for (let k = e.firstChild; k; k = k.nextSibling) {
      if (k.nodeType === 3) {
        const t = k as Text;
        knoten.push(t);
        for (let o = 0; o < t.data.length; o++) {
          if (/\s/.test(t.data[o])) {
            if (!leer) { text += ' '; stellen.push([t, o]); leer = true; }
          } else { text += t.data[o]; stellen.push([t, o]); leer = false; }
        }
      } else if (k.nodeType === 1) {
        const x = k as Element;
        if (OHNE_TEXT.has(x.localName) || (x.localName === 'del' && x.classList.contains('za-weg'))) continue;
        if (x.localName === 'br' && !leer) { text += ' '; stellen.push(null); leer = true; }
        laufen(x);
      }
    }
  };
  laufen(el);
  return { text, stellen, knoten };
}

/**
 * Markiert die erste Fundstelle von `zitat` im Block (über Textknoten hinweg)
 * mit `<mark class="za-zitat" data-za-kommentar="<id>">`. `anker.text` (falls
 * vorhanden) hilft, den Block wiederzufinden, wenn sich die Nummer verschoben
 * hat. Rückgabe: erste Markierung oder null.
 */
export function zitatMarkieren(doc: Document, anker: BlockSchluessel & { text?: string }, zitat: string, id: string): HTMLElement | null {
  const z = normalisieren(zitat ?? '');
  if (!z || !anker) return null;
  stilEinfuegen(doc);
  const bloecke = textbloecke(doc);
  const enthaelt = (b: TextBlock) => textKarte(b.el).text.includes(z);
  const amOrt = bloecke.find((b) => b.schluessel.tag === anker.tag && b.schluessel.nr === anker.nr);
  let block: TextBlock | undefined;
  if (amOrt && ((anker.text !== undefined && ankerTextPasst(amOrt.text, anker.text)) || enthaelt(amOrt))) block = amOrt;
  if (!block && anker.text) {
    const t = bloecke.filter((b) => b.schluessel.tag === anker.tag && ankerTextPasst(b.text, anker.text!));
    if (t.length === 1 && enthaelt(t[0])) block = t[0];
  }
  block ??= bloecke.find(enthaelt);
  if (!block) return null;

  const karte = textKarte(block.el);
  let von = karte.text.indexOf(z);
  if (von < 0) return null;
  let bis = von + z.length - 1;
  while (von <= bis && !karte.stellen[von]) von++;
  while (bis >= von && !karte.stellen[bis]) bis--;
  if (von > bis) return null;
  const [startKnoten, startOff] = karte.stellen[von]!;
  const [endKnoten, endOff] = karte.stellen[bis]!;
  const a = karte.knoten.indexOf(startKnoten);
  const e = karte.knoten.indexOf(endKnoten);
  const marken: HTMLElement[] = [];
  for (let k = a; k <= e; k++) {
    let t = karte.knoten[k];
    const anf = k === a ? startOff : 0;
    const end = k === e ? endOff + 1 : t.data.length;
    if (anf >= end) continue;
    if (anf > 0) t = t.splitText(anf);
    if (end - anf < t.data.length) t.splitText(end - anf);
    const m = doc.createElement('mark');
    m.className = 'za-zitat';
    m.setAttribute('data-za-kommentar', id);
    t.parentNode!.insertBefore(m, t);
    m.appendChild(t);
    marken.push(m);
  }
  return marken[0] ?? null;
}

/** Entfernt alle Zitat-Markierungen wieder. */
export function zitateEntfernen(doc: Document): void {
  doc.querySelectorAll('mark.za-zitat').forEach((m) => {
    const p = m.parentNode;
    if (!p) return;
    while (m.firstChild) p.insertBefore(m.firstChild, m);
    m.remove();
    p.normalize();
  });
}
